import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/app/NotificationBell";
import { useMyMenus } from "@/hooks/useMyMenus";
import {
  LayoutDashboard, Users, ClipboardList, FileText, Briefcase,
  LifeBuoy, MessageSquare, LogOut, ShieldCheck, Package, Wallet, Wrench, CalendarDays, Menu, X, HardHat, Activity, ClipboardCheck, Layers, Lock, Files, UserCheck, MapPin, ChevronDown, Building2,
  Store, Boxes, Tag, Receipt, ShoppingCart, PackageCheck, LineChart, BarChart3, ArrowUpDown, AlertTriangle,
} from "lucide-react";

const COLLAPSE_STORAGE_KEY = "sidebar:collapsed_sections:v1";

// Grouped navigation. Sections with `title: null` render as ungrouped items
// (no header). Each item is filtered by user role + menu permissions before
// display; empty sections auto-hide their header.
const NAV_SECTIONS = [
  {
    title: null,
    items: [
      { to: "/", key: "dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
      { to: "/my-assignments", key: "my-assignments", label: "My Assignments", icon: UserCheck, testid: "nav-my-assignments" },
    ],
  },
  {
    title: "Sales Wing",
    items: [
      { to: "/leads", key: "leads", label: "Leads & CRM", icon: Users, testid: "nav-leads" },
      { to: "/calendar", key: "calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
      { to: "/sales-visits", key: "sales-visits", label: "Sales Site Visits", icon: MapPin, testid: "nav-sales-visits" },
      { to: "/proposals", key: "proposals", label: "Proposals", icon: FileText, testid: "nav-proposals" },
      { to: "/system-templates", key: "system-templates", label: "Templates", icon: Layers, testid: "nav-system-templates" },
    ],
  },
  {
    title: "Project Execution",
    items: [
      { to: "/projects", key: "projects", label: "Projects", icon: Briefcase, testid: "nav-projects" },
      { to: "/surveys", key: "surveys", label: "Site Surveys", icon: ClipboardList, testid: "nav-surveys" },
      { to: "/operations", key: "operations", label: "Operations", icon: Activity, testid: "nav-operations" },
      { to: "/work-scheduler", key: "work-scheduler", label: "Work Scheduler", icon: HardHat, testid: "nav-work-scheduler" },
      { to: "/delivery-checklists", key: "delivery-checklists", label: "Delivery Checklists", icon: ClipboardCheck, testid: "nav-delivery-checklists" },
      { to: "/documentation-tracking", key: "documentation-tracking", label: "Documentation Tracking", icon: Files, testid: "nav-documentation-tracking" },
    ],
  },
  {
    title: "Finance and Stock",
    items: [
      { to: "/finance", key: "finance", label: "Finance", icon: Wallet, testid: "nav-finance" },
      { to: "/stock", key: "stock", label: "Stock", icon: Package, end: true, testid: "nav-stock" },
      { to: "/stock/current", key: "stock", label: "Current Stock", icon: Package, testid: "nav-stock-current" },
      { to: "/stock/ledger", key: "stock", label: "Stock Ledger", icon: Package, testid: "nav-stock-ledger" },
      { to: "/stock/low", key: "stock", label: "Low Stock", icon: AlertTriangle, testid: "nav-stock-low" },
      { to: "/stock/reports", key: "stock", label: "Reports", icon: BarChart3, testid: "nav-stock-reports" },
    ],
  },
  {
    title: "Purchasing",
    items: [
      { to: "/purchasing", key: "purchasing", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-purchasing" },
      { to: "/purchasing/vendors", key: "vendors", label: "Vendors", icon: Store, testid: "nav-vendors" },
      { to: "/purchasing/products", key: "products", label: "Products", icon: Boxes, testid: "nav-products" },
      { to: "/purchasing/brands", key: "brands", label: "Brands", icon: Tag, testid: "nav-brands" },
      { to: "/purchasing/price-enquiries", key: "price-enquiries", label: "Price Enquiries", icon: Receipt, testid: "nav-price-enquiries" },
      { to: "/purchasing/purchase-orders", key: "purchase-orders", label: "Purchase Orders", icon: ShoppingCart, testid: "nav-purchase-orders" },
      { to: "/purchasing/grn", key: "grn", label: "Goods Receipt", icon: PackageCheck, testid: "nav-grn" },
      { to: "/purchasing/comparison", key: "purchase-orders", label: "Product Comparison", icon: ArrowUpDown, testid: "nav-comparison" },
      { to: "/purchasing/vendor-performance", key: "purchasing", label: "Vendor Performance", icon: BarChart3, testid: "nav-vendor-performance" },
      { to: "/purchasing/price-history", key: "price-history", label: "Vendor Price History", icon: LineChart, testid: "nav-price-history" },
      { to: "/purchasing/reports", key: "purchasing-reports", label: "Reports", icon: BarChart3, testid: "nav-purchasing-reports" },
    ],
  },
  {
    title: "Customer Service",
    items: [
      { to: "/amc", key: "amc", label: "AMC", icon: Wrench, testid: "nav-amc" },
      { to: "/tickets", key: "tickets", label: "Service Tickets", icon: LifeBuoy, testid: "nav-tickets" },
      { to: "/whatsapp", key: "whatsapp", label: "WhatsApp Log", icon: MessageSquare, testid: "nav-whatsapp" },
    ],
  },
  {
    title: "Admin",
    items: [
      { to: "/users", key: "users", label: "Users", icon: ShieldCheck, testid: "nav-users", adminOnly: true },
      { to: "/permissions", key: "permissions", label: "Permissions", icon: Lock, testid: "nav-permissions", adminOnly: true },
      { to: "/general-settings", key: "general-settings", label: "General Settings", icon: Building2, testid: "nav-general-settings", adminOnly: true },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { menus } = useMyMenus(user?.role);
  const handleLogout = async () => { await logout(); nav("/login"); };

  // Filter helper — respects adminOnly and per-user menu permissions.
  const isVisible = (n) => {
    if (n.adminOnly) return user?.role === "admin";
    if (!menus) return user?.role === "admin"; // while loading, admin sees all; others see none
    return menus.has(n.key);
  };
  const sections = NAV_SECTIONS
    .map((s) => ({ ...s, items: s.items.filter(isVisible) }))
    .filter((s) => s.items.length > 0);
  const flatItems = sections.flatMap((s) => s.items);
  const currentLabel = flatItems.find((n) => n.to === location.pathname || (n.end && location.pathname === "/"))?.label
    || flatItems.find((n) => location.pathname.startsWith(n.to) && n.to !== "/")?.label
    || "Menu";

  // Collapsible groups — persisted per user in localStorage.
  const storageKey = `${COLLAPSE_STORAGE_KEY}:${user?.id || "anon"}`;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify([...collapsed])); } catch { /* ignore quota */ }
  }, [collapsed, storageKey]);
  const toggleSection = (title) => {
    if (!title) return;
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title); else next.add(title);
      return next;
    });
  };
  // Auto-expand the section containing the current route so users don't get
  // confused by a hidden active item after refresh.
  useEffect(() => {
    const active = sections.find((s) =>
      s.title && s.items.some((n) =>
        n.to === location.pathname || (n.end && location.pathname === "/") ||
        (n.to !== "/" && location.pathname.startsWith(n.to))
      )
    );
    if (active && collapsed.has(active.title)) {
      setCollapsed((prev) => {
        const next = new Set(prev);
        next.delete(active.title);
        return next;
      });
    }
  }, [location.pathname]);

  // Render sidebar body as inline JSX via a plain function (not a nested
  // component) so React doesn't unmount + remount the whole subtree on every
  // parent render. Previously `SidebarBody` was declared inside Layout, which
  // gave it a fresh component identity per render → the internal <nav>'s
  // scrollTop was reset to 0 on every route change, making the sidebar
  // "jump to top" whenever a menu item was clicked.
  const renderSidebarBody = (onItemClick) => (
    <>
      <div className="p-6 border-b border-[#E5E7EB] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_helios-platform-2/artifacts/zblm1o4j_Multimix%20Energy%20Pvt%20Ltd%20Logo%20%28150%20x%2050%20mm%29%20%281%29.png"
            alt="Multimix Energy"
            className="h-12 w-auto object-contain"
          />
          <div className="label-eyebrow !text-[9px] leading-tight">Solar<br />ERP + CRM</div>
        </div>
        <button
          onClick={() => setMobileOpen(false)}
          className="lg:hidden p-1"
          data-testid="mobile-nav-close"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 py-4 overflow-y-auto" data-testid="sidebar-nav">
        {sections.map((section, sIdx) => {
          const isCollapsed = section.title && collapsed.has(section.title);
          const slug = section.title ? section.title.toLowerCase().replace(/[^a-z0-9]+/g, "-") : `_g${sIdx}`;
          return (
            <div key={section.title || `_group_${sIdx}`} className={sIdx > 0 ? "mt-4" : ""}>
              {section.title && (
                <button
                  type="button"
                  onClick={() => toggleSection(section.title)}
                  className="w-full flex items-center justify-between gap-2 px-6 pt-2 pb-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-[#9CA3AF] hover:text-[#0a0a0a] transition-colors group"
                  data-testid={`nav-section-${slug}`}
                  aria-expanded={!isCollapsed}
                >
                  <span>{section.title}</span>
                  <ChevronDown
                    className={`w-3 h-3 transition-transform duration-150 ${isCollapsed ? "-rotate-90" : "rotate-0"}`}
                    strokeWidth={2.5}
                  />
                </button>
              )}
              {!isCollapsed && section.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={n.testid}
                  onClick={onItemClick}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
                      isActive
                        ? "bg-[#F9FAFB] border-[#002FA7] text-[#0a0a0a] font-semibold"
                        : "border-transparent text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#0a0a0a]"
                    }`
                  }
                >
                  <n.icon className="w-4 h-4" strokeWidth={2} />
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      <div className="p-4 border-t border-[#E5E7EB]">
        <div className="text-xs space-y-0.5 mb-3">
          <div className="font-semibold text-[#0a0a0a] truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="label-eyebrow !tracking-[0.18em]">{user?.role}</div>
        </div>
        <Button
          onClick={handleLogout}
          variant="outline"
          className="w-full rounded-none border-[#0a0a0a] text-xs uppercase tracking-widest font-bold"
          data-testid="logout-button"
        >
          <LogOut className="w-3 h-3 mr-2" /> Sign out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-white">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-shrink-0 bg-white border-r border-[#E5E7EB] flex-col sticky top-0 h-screen">
        {renderSidebarBody()}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-nav-backdrop"
          />
          <aside className="relative w-72 max-w-[85vw] bg-white border-r border-[#E5E7EB] flex flex-col h-full shadow-2xl">
            {renderSidebarBody(() => setMobileOpen(false))}
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 bg-white">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-[#E5E7EB] flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-1.5 border border-[#0a0a0a]"
            data-testid="mobile-nav-toggle"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <img
              src="https://customer-assets.emergentagent.com/job_helios-platform-2/artifacts/zblm1o4j_Multimix%20Energy%20Pvt%20Ltd%20Logo%20%28150%20x%2050%20mm%29%20%281%29.png"
              alt="Multimix Energy"
              className="h-7 w-auto object-contain"
            />
            <div className="label-eyebrow !text-[10px] truncate">{currentLabel}</div>
          </div>
          <NotificationBell />
        </div>

        {/* Desktop top bar */}
        <div className="hidden lg:flex sticky top-0 z-30 bg-white border-b border-[#E5E7EB] items-center justify-end gap-3 px-8 py-2">
          <span className="text-xs text-[#4B5563]">{user?.name} · <span className="uppercase tracking-widest font-bold">{user?.role}</span></span>
          <NotificationBell />
        </div>

        <Outlet />
      </main>
    </div>
  );
}
